import React from "react";
import { IoCheckmarkCircle } from "react-icons/io5";
import { MdOutlineHandshake } from "react-icons/md";

const EnvironmentalPolicy = () => {
  const policyPoints = [
    "Comply with applicable national and international environmental and social management regulations as relates to environmental sustainability, labour and working conditions as well as Community, health safety and security;",
    "Reduce fossil-fuel use, water consumption and waste generation;",
    "Create technically credible and positive environmental, social and economic benefits for the communities where we do business;",
    "Develop a program to raise employee awareness of environmental issues and promote responsible behavior, thereby helping employees to positively impact the environment in their work lives;",
    "Incorporate sustainable design into new projects, as well as during operations and maintenance of our assets to improve our overall efficiency and environmental performance;",
    "Incorporate environmental and social sustainability principles as an integral part of our procurement process and ensure contractors and suppliers also comply;",
    "Track, measure and decrease our overall resource use in line with global best practices;",
    "Continuously engage relevant stakeholders in areas where we do business."
  ];

  return (
    <div className="w-full min-h-screen bg-white font-sans">
      {/* Hero Section */}
      <div className="relative bg-[#14532d] text-white py-12 md:py-16 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px'
          }}> 
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 relative z-10">
          <div className="flex flex-col lg:flex-row items-center justify-between">
            <div className="lg:w-2/3">
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight uppercase tracking-tight">
              Environmental <span className="text-yellow-400">Policy</span>
              </h1>
              <div className="w-20 h-1.5 bg-yellow-400 rounded-full mb-6"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 md:py-8">
        <div className="bg-black text-white p-4 sm:p-6 md:p-8 mb-6 shadow-xl rounded-xl md:rounded-2xl border-l-[6px] md:border-l-[8px] border-green-700 relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-64 h-64 bg-green-700/10 rounded-full -mr-32 -mt-32 group-hover:scale-110 transition-transform duration-700"></div>
          <p className="text-sm sm:text-base md:text-lg leading-relaxed font-bold relative z-10 italic">
          BEDC Electricity PLC is committed to reducing its environmental impact and upholding social responsibility across its offices, business units and facilities locations.
          </p>
        </div>

        {/* Core Vision Section */}
        <div className="bg-white rounded-xl md:rounded-2xl shadow-xl border border-gray-100 overflow-hidden mb-6">
          <div className="p-4 sm:p-6 md:p-8">
            <div className="flex items-center gap-3 mb-4">
              <h2 className="text-lg sm:text-xl md:text-2xl font-black text-black uppercase tracking-tighter">
                Our <span className="text-green-700">Environment</span>
              </h2>
            </div>

            <p className="text-gray-600 text-xs sm:text-sm md:text-base font-medium leading-relaxed mb-6">
              We believe that environmental and social compliance is the responsibility of both management and employees and should be critical to the manner we conduct our business. The Company will monitor and drive compliance efforts through awareness, self-assessments, audits and taking corrective actions as needed.
            </p>

            {/* ✅ Properly Centered Image */}
            <div className="relative rounded-xl overflow-hidden shadow-xl border-4 border-white ring-1 ring-gray-100 transform hover:scale-[1.01] transition-transform duration-500 mb-6">
              <img
                src="/images/background-2.png"
                alt="BEDC Environmental Ops"
                className="w-full h-auto object-cover max-h-[350px]"
              />
            </div>

            {/* Green HSE Banner Integration */}
            <div className="bg-green-700 p-6 sm:p-8 rounded-xl md:rounded-2xl shadow-xl relative overflow-hidden mb-6 group">
              <div className="absolute top-0 right-0 w-64 h-64 bg-black/10 rounded-full -mr-32 -mt-32 group-hover:scale-110 transition-transform duration-700"></div>
              <div className="max-w-3xl relative z-10">
                <span className="text-white/80 text-[10px] sm:text-xs font-black uppercase tracking-[0.2em] block mb-2">
                  Health • Safety • Environment
                </span>
                <h3 className="text-white text-xl sm:text-2xl md:text-3xl font-black leading-tight uppercase tracking-tighter mb-4">
                  Building a <span className="text-yellow-400">Sustainable</span> Future
                </h3>
                <div className="w-16 h-1.5 bg-yellow-400 mb-4 rounded-full" />
                <p className="text-white text-xs sm:text-sm md:text-base font-medium leading-relaxed opacity-90">
                  BEDC is committed to building safe environments and eco-friendly solutions while fostering strong relationships across all communities we serve.
                </p>
              </div>
            </div>

            <div className="mb-6">
              <img
                src="/images/background-3.png"
                alt="BEDC Environmental Ops"
                className="w-full h-auto object-cover rounded-xl max-h-[350px]"
              />
            </div>
            
            {/* Refined Policy List */}
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-yellow-400 p-2.5 rounded-lg shadow-md">
                  <MdOutlineHandshake className="text-black text-xl sm:text-2xl" />
                </div>
                <h4 className="text-base sm:text-lg md:text-xl font-black text-black uppercase tracking-tight">
                  Sustainability Commitments
                </h4>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {policyPoints.map((point, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 sm:p-5 rounded-xl bg-gray-50 border border-gray-100 hover:bg-white hover:border-green-700 hover:shadow-lg transition-all duration-300 group">
                    <div className="mt-0.5 flex-shrink-0 bg-white p-1.5 rounded-lg shadow-sm group-hover:bg-green-700 transition-colors">
                      <IoCheckmarkCircle className="text-green-700 text-lg sm:text-xl group-hover:text-white" />
                    </div>
                    <p className="text-black leading-relaxed font-bold text-xs sm:text-sm">
                      {point}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Final Statement */}
            <div className="mt-6 p-6 sm:p-8 bg-black rounded-xl md:rounded-2xl text-white shadow-xl relative overflow-hidden group">
              <div className="absolute inset-0 bg-green-700/10 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
              <p className="leading-relaxed text-xs sm:text-sm md:text-base font-medium italic relative z-10">
                "Management will be responsible for providing leadership in the implementation of this policy and maintain a work environment that values and ensures these practices and principles are seen as a responsibility of all employees and relevant stakeholders."
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnvironmentalPolicy;